import { Button } from '@/components/Button';
import DropdownMenu from '@/components/DropdownMenu';
import Text from '@/components/Text';
import { faArrowTrendUp, faPlus, faTableColumns, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    DataStreamBaseTileConfig,
    FormattedStreamValue,
    ShapeName,
    StreamPrimitive,
    date,
    getColumnShape,
    getShape,
    isNone
} from '@squaredup/data-streams';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { BehindFlag } from 'components/BehindFlag';
import { TruncatedText } from 'components/TruncatedText';
import { useDOMElement } from 'components/hooks/useDOMElement';
import Tooltip from 'components/tooltip/Tooltip';
import { renderCellValue } from 'dashboard-engine/visualisations/DataStreamTable/RenderCell';
import stringify from 'fast-json-stable-stringify';
import { orderBy, toString, upperFirst } from 'lodash';
import { ControlledApplicationTable, useApplicationTable } from 'pages/components/ApplicationTable/ApplicationTable';
import { Action } from 'pages/components/ApplicationTable/types';
import { Dispatch, FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { ComparisonForm, MetadataEditorComparisonModal } from './MetadataEditorComparisonModal';
import { RowMetadataModal } from './MetadataEditorModal';
import {
    DataStreamMetadataAction,
    columnCanBeRemoved,
    getCloneDisplayName,
    getCloneNumber,
    getComparisonDisplayName,
    getComparisonNumber
} from './MetadataEditorReducer';
import {
    CloneStateColumn,
    ComparisonStateColumn,
    ComputedStateColumn,
    DataStreamMetadataState,
    DataStreamMetadataStateColumn,
    StateColumn,
    isNonPatternColumn
} from './MetadataEditorState';
import { findClones, findComparisons } from './MetadataEditorTileState';

export type MetadataTableRow = {
    name: string;
    hasMetadata: boolean;
    isCloneable: boolean;
    isComparable: boolean;
    isBeingCloned: boolean;
    isComparison: boolean;
    isComputed: boolean;
    failedConversion: boolean;
    isOverridden: boolean;
    displayName: {
        value: string;
        field: ReactNode;
    };
    sourceColumn?: {
        name: string;
        displayName: string;
    };
    shape: string;
    sampleValue: {
        value: StreamPrimitive;
        field: ReactNode;
    };
    sampleFormattedValue: {
        value: string;
        field: ReactNode;
    };
};

const DisplayNameColumn: FC<{
    displayName: string;
    isOverridden: boolean;
}> = ({ displayName, isOverridden }) => {
    return (
        <div className='flex items-center'>
            <div className='inline-block'>
                <div className='grid items-center justify-start grid-flow-col grid-cols-[auto_auto_1rem] gap-x-1 group/displayname'>
                    <TruncatedText title={displayName} className='truncate'>
                        {displayName}
                    </TruncatedText>
                    {isOverridden && (
                        <Text.SmallBody className='not-italic text-textSecondary'>(edited)</Text.SmallBody>
                    )}
                </div>
            </div>
        </div>
    );
};

export const getColumnsInDisplayOrder = (
    state: DataStreamMetadataState,
    rootColumns?: (StateColumn | CloneStateColumn | ComparisonStateColumn | ComputedStateColumn)[]
): DataStreamMetadataStateColumn[] => {
    rootColumns ??= [...state.columnMap.values()].filter(
        (c): c is StateColumn => !c.isPattern && !c.isClone && !c.isComparison
    );

    /**
     * If we don't have defined metadata, the order of the overrides will change the
     * displayIndex of the columns, which we don't want.
     *
     * By not sorting we keep the existing order which was set when the state was created.
     * This avoids the UI jumping around unexpectedly.
     */
    const order = state.hasDefinedMetadata
        ? (c: StateColumn | CloneStateColumn | ComparisonStateColumn | ComputedStateColumn) =>
              c.metadata?.displayIndex ?? Number.MAX_SAFE_INTEGER
        : () => Number.MAX_SAFE_INTEGER;

    const sortedColumns = orderBy(rootColumns, order);

    return sortedColumns.flatMap((c) => [
        c,
        ...getColumnsInDisplayOrder(state, [
            ...findClones(state, c.columnName),
            ...findComparisons(state, c.columnName)
        ])
    ]);
};

/**
 * Override how an object is serialised to JSON to avoid circular references
 */
const makeSerialisable = (object: { value: string; field: ReactNode }): { value: string; field: ReactNode } => {
    const serialisable = { ...object, toJSON: () => object.value };

    return serialisable;
};

const getCommonRowProps = (
    columnName: string,
    column: StateColumn | CloneStateColumn | ComparisonStateColumn | ComputedStateColumn,
    sampleValue: FormattedStreamValue
): Omit<MetadataTableRow, 'displayName'> => {
    const shapeName: ShapeName | undefined =
        getColumnShape(column.metadataOverrides?.shape)?.name ?? column.metadata?.shapeName;

    const FormattedValue = shapeName ? (
        <div className='w-min max-w-72' data-testid='formattedValue'>
            {renderCellValue(shapeName, sampleValue, { align: 'left' })}
        </div>
    ) : (
        <></>
    );

    return {
        name: columnName,
        hasMetadata: column.metadata != null,
        isCloneable: column.isCloneable,
        isComparable: column.isComparable,
        isBeingCloned: column.isClone && column.isBeingCloned,
        isComparison: column.isComparison,
        isComputed: column.isComputed,
        failedConversion: isNone(sampleValue.value),
        isOverridden:
            // We don't want to show the 'edited' indicator if the only override is an expand (clone)
            Object.keys(column.metadataOverrides ?? {}).filter((k) => k !== 'expand' && k !== 'name').length > 0,
        shape: shapeName ?? '',
        sampleValue: makeSerialisable({
            value: toString(sampleValue.raw),
            field: <TruncatedText title={toString(sampleValue.raw)}></TruncatedText>
        }),
        sampleFormattedValue: makeSerialisable({
            value: sampleValue.formatted,
            field: FormattedValue
        })
    };
};

export const toTableData = (
    state: DataStreamMetadataState,
    getSampleValue: (columnName: string) => FormattedStreamValue,
    dispatch: Dispatch<DataStreamMetadataAction>
): MetadataTableRow[] => {
    return getColumnsInDisplayOrder(state)
        .filter(isNonPatternColumn)
        .filter((c) => !c.isClone || c.isEditable)
        .map((column): MetadataTableRow => {
            const columnDisplayName = column.metadataOverrides?.displayName ?? column.metadata?.displayName;
            const commonRowProps = getCommonRowProps(column.columnName, column, getSampleValue(column.columnName));

            if (column.isClone) {
                const cloneSource = state.columnMap.get(column.sourceColumnName);
                const cloneSourceColumn = cloneSource?.isPattern ? undefined : cloneSource;

                const cloneSourceDisplayName =
                    cloneSourceColumn?.metadataOverrides?.displayName ??
                    cloneSourceColumn?.metadata?.displayName ??
                    column.sourceColumnName;

                if (column.isBeingCloned) {
                    return {
                        name: column.columnName,
                        hasMetadata: false,
                        isCloneable: false,
                        isComparable: false,
                        isBeingCloned: column.isClone && column.isBeingCloned,
                        isComparison: false,
                        isComputed: false,
                        failedConversion: false,
                        isOverridden: false,
                        displayName: makeSerialisable({
                            value: `Copying ${cloneSourceDisplayName}...`,
                            field: (
                                <TruncatedText title={`Copying ${cloneSourceDisplayName}...`}>
                                    Copying {cloneSourceDisplayName}...
                                </TruncatedText>
                            )
                        }),
                        shape: '',
                        sampleValue: { value: '', field: '' },
                        sampleFormattedValue: { value: '', field: '' }
                    };
                }

                const cloneNumber = z.number({ coerce: true }).safeParse(getCloneNumber(column.cloneName));

                // If we're resetting the display name of a clone, set it to the generated
                // display name override, otherwise we'll get the not-very-useful inferred name
                const defaultDisplayName = getCloneDisplayName(
                    state.columnMap.get(column.sourceColumnName) as StateColumn | CloneStateColumn,
                    cloneNumber.success ? cloneNumber.data - 1 : 0
                );

                return {
                    ...commonRowProps,
                    displayName: makeSerialisable({
                        value: columnDisplayName ?? column.columnName,
                        field: (
                            <>
                                {column.metadataOverrides?.displayName !== defaultDisplayName && (
                                    <TruncatedText title={defaultDisplayName}>
                                        <Text.SmallBody className='text-secondary'>{defaultDisplayName}</Text.SmallBody>
                                    </TruncatedText>
                                )}
                                <DisplayNameColumn
                                    displayName={columnDisplayName ?? defaultDisplayName}
                                    isOverridden={commonRowProps.isOverridden}
                                />
                            </>
                        )
                    })
                };
            }

            if (column.isComparison) {
                const comparisonSource = state.columnMap.get(column.sourceColumnName);
                const comparisonSourceColumn = comparisonSource?.isPattern ? undefined : comparisonSource;

                const comparisonSourceDisplayName =
                    comparisonSourceColumn?.metadataOverrides?.displayName ??
                    comparisonSourceColumn?.metadata?.displayName ??
                    column.sourceColumnName;

                const comparisonNumber = z
                    .number({ coerce: true })
                    .safeParse(getComparisonNumber(column.comparisonName));

                // If we're resetting the display name of a comparison, set it to the generated
                // display name override, otherwise we'll get the not-very-useful inferred name
                const defaultDisplayName = getComparisonDisplayName(
                    state.columnMap.get(column.sourceColumnName) as StateColumn | ComparisonStateColumn,
                    comparisonNumber.success ? comparisonNumber.data - 1 : 0
                );

                return {
                    ...commonRowProps,
                    sourceColumn: {
                        name: column.sourceColumnName,
                        displayName: comparisonSourceDisplayName
                    },
                    displayName: makeSerialisable({
                        value: columnDisplayName ?? column.columnName,
                        field: (
                            <>
                                {column.metadataOverrides?.displayName !== defaultDisplayName && (
                                    <TruncatedText title={defaultDisplayName}>
                                        <Text.SmallBody className='text-secondary'>{defaultDisplayName}</Text.SmallBody>
                                    </TruncatedText>
                                )}
                                <DisplayNameColumn
                                    displayName={columnDisplayName ?? defaultDisplayName}
                                    isOverridden={commonRowProps.isOverridden}
                                />
                            </>
                        )
                    })
                };
            }

            return {
                ...commonRowProps,
                displayName: makeSerialisable({
                    value: columnDisplayName ?? column.columnName,
                    field: (
                        <DisplayNameColumn
                            displayName={columnDisplayName ?? ''}
                            isOverridden={commonRowProps.isOverridden}
                        />
                    )
                })
            };
        });
};

const columnHelper = createColumnHelper<MetadataTableRow>();
const tableColumns: ColumnDef<MetadataTableRow, string>[] = [
    columnHelper.accessor((row) => row.displayName.value, {
        id: 'displayName',
        header: 'Name',
        cell: ({ row }) => row.original.displayName.field,
        size: 32
    }),
    columnHelper.accessor((row) => row.shape, {
        header: 'Type',
        id: 'shape',
        cell: ({ row }) => {
            return upperFirst(getShape(row.original.shape as ShapeName)?.displayName);
        },
        size: 22
    }),
    columnHelper.accessor((row) => row.sampleValue.value, {
        header: 'Value',
        id: 'sampleValue',
        cell: ({ row }) => {
            return row.original.sampleValue.field;
        },
        size: 22,
        maxSize: 18
    }),
    columnHelper.accessor((row) => row.sampleFormattedValue.value, {
        header: 'Formatted',
        id: 'sampleFormattedValue',
        cell: ({ row }) => {
            if (row.original.failedConversion) {
                const shape = getShape(row.original.shape as ShapeName);
                const shapeMessage = shape != null ? ` to ${shape.displayName}` : '';

                const actionMessage =
                    shape === date
                        ? '\n\nSet Input Format to match the raw values or choose a different shape.'
                        : '\n\nTry a different shape.';
                return (
                    <div className='flex items-center gap-4'>
                        <Tooltip title={`Failed to convert${shapeMessage}.${actionMessage}`}>
                            <FontAwesomeIcon
                                icon={faTriangleExclamation}
                                className='cursor-pointer text-statusWarningPrimary'
                            />
                        </Tooltip>
                        {row.original.sampleFormattedValue.field}
                    </div>
                );
            }
            return row.original.sampleFormattedValue.field;
        },
        size: 22,
        maxSize: 18
    })
];

export const MetadataEditorTable: FC<{
    state: DataStreamMetadataState;
    config: DataStreamBaseTileConfig;
    getSampleValue: (columnName: string) => FormattedStreamValue;
    dispatch: Dispatch<DataStreamMetadataAction>;
}> = ({ state, config, getSampleValue, dispatch }) => {
    const [existingComparison, setExistingComparison] = useState<ComparisonForm | undefined>();
    const [selectedComparisonColumn, setSelectedComparisonColumn] = useState<string | undefined>();
    const [addingComparisonColumn, setAddingComparisonColumn] = useState<boolean>();

    const [openModalRowId, setOpenModalRowId] = useState<string>();
    const [cloningColumn, setCloningColumn] = useState<CloneStateColumn>();
    const stateString = stringify(state);
    const addColumnContainer = useDOMElement('metadataStep');

    const actions = useMemo(
        (): Action[] => {
            return [
                {
                    icon: 'arrow-trend-up',
                    visible: (row: MetadataTableRow) => row.isComparison,
                    dataTestId: 'editComparisonButton',
                    tooltip: 'Edit comparison',
                    action: (row: MetadataTableRow) => {
                        const comparisonColumn = state.columnMap.get(row.name) as ComparisonStateColumn;
                        if (
                            !(
                                comparisonColumn.metadataOverrides &&
                                'comparisonType' in comparisonColumn.metadataOverrides
                            )
                        ) {
                            return;
                        }
                        const metadata = comparisonColumn.metadataOverrides;
                        setExistingComparison({
                            comparisonName: row.name,
                            comparisonType: metadata.comparisonType ?? 'absolute',
                            sourceColumn: comparisonColumn.sourceColumnName,
                            compareTo: metadata.compareTo ?? '',
                            shape: comparisonColumn.shape
                        });
                    },
                    loading: () => {
                        return false;
                    }
                },
                {
                    icon: 'arrow-trend-up',
                    visible: (row: MetadataTableRow) => row.isComparable,
                    dataTestId: 'addComparisonButton',
                    tooltip: 'Add comparison',
                    action: (row: MetadataTableRow) => {
                        setSelectedComparisonColumn(row.name);
                    },
                    loading: () => {
                        return false;
                    }
                },
                {
                    icon: 'clone',
                    visible: (row: MetadataTableRow) => row.isCloneable,
                    tooltip: 'Clone column',
                    dataTestId: 'cloneButton',
                    action: (row: MetadataTableRow) => {
                        dispatch({ type: 'cloneColumn', sourceColumnName: row.name });
                    },
                    loading: (row: MetadataTableRow) => {
                        const foundColumn = state.columnMap.get(row.name);

                        if (!foundColumn || foundColumn.isPattern) {
                            return false;
                        }

                        const isBeingCloned = findClones(state, foundColumn.columnName).some((c) => c.isBeingCloned);

                        return isBeingCloned;
                    }
                },
                {
                    icon: 'trash',
                    visible: ({ name }: MetadataTableRow) => {
                        return columnCanBeRemoved(state, name);
                    },
                    tooltip: 'Delete column',
                    dataTestId: 'deleteButton',
                    action: (column: MetadataTableRow) => {
                        dispatch({ type: 'removeColumn', columnName: column.name });
                    }
                },
                {
                    icon: 'pencil',
                    tooltip: 'Configure column',
                    dataTestId: 'editButton',
                    action: (row: MetadataTableRow) => {
                        setOpenModalRowId(row.name);
                    }
                }
            ];
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, stateString]
    );

    const tableData = useMemo(
        () => toTableData(state, getSampleValue, dispatch),
        [dispatch, getSampleValue, stateString] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const columns = [...state.columnMap.values()].filter(isNonPatternColumn);

    const tableProps = useApplicationTable({
        classNames: { actionColumn: 'from-tileBackground', tableHeader: 'z-10' },
        config: { actions },
        onRowClick: (row) => {
            setOpenModalRowId(row.id);
        },
        columns: tableColumns,
        data: tableData,
        getRowId: (row) => row.name,
        hiddenActions: 'collapse',
        layoutVersion: 'v2',
        defaultColumn: {
            minSize: 10 // in rem
        }
    });

    useEffect(() => {
        if (state.tileConfigUpdatePending) {
            const column = state.columnMap.values().find((col) => col.isClone && col.isBeingCloned) as
                | CloneStateColumn
                | undefined;
            if (column) {
                setCloningColumn(column);
            }
        } else if (
            cloningColumn &&
            !(
                state.columnMap
                    .values()
                    .find((col) => col.isClone && col.columnName === cloningColumn.columnName) as CloneStateColumn
            )?.isBeingCloned
        ) {
            setOpenModalRowId(cloningColumn.columnName);
            setCloningColumn(undefined);
        }
    }, [state, cloningColumn]);

    return (
        <>
            <ControlledApplicationTable {...tableProps} />

            <BehindFlag flagName='computedColumns'>
                <div className='py-4'>
                    <DropdownMenu>
                        <DropdownMenu.Trigger className='whitespace-nowrap'>
                            <Button variant='secondary' icon={<FontAwesomeIcon icon={faPlus} />}>
                                <span>Add</span>
                            </Button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Menu align='start' container={addColumnContainer}>
                            <DropdownMenu.Group>
                                <DropdownMenu.Item
                                    onSelect={(e) => {
                                        e.stopPropagation();
                                        dispatch({ type: 'addComputedColumn' });
                                    }}
                                >
                                    <div className='flex'>
                                        <div className='w-5 h-5 mr-3'>
                                            <FontAwesomeIcon icon={faTableColumns} fixedWidth />
                                        </div>
                                        Column
                                    </div>
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                    onSelect={(e) => {
                                        e.stopPropagation();
                                        setAddingComparisonColumn(true);
                                    }}
                                >
                                    <div className='flex'>
                                        <div className='w-5 h-5 mr-3'>
                                            <FontAwesomeIcon icon={faArrowTrendUp} fixedWidth />
                                        </div>
                                        Comparison
                                    </div>
                                </DropdownMenu.Item>
                            </DropdownMenu.Group>
                        </DropdownMenu.Menu>
                    </DropdownMenu>
                </div>
            </BehindFlag>

            {openModalRowId && (
                <RowMetadataModal
                    columns={columns}
                    row={tableProps.table.getRow(openModalRowId).original!}
                    state={state}
                    config={config}
                    dispatch={dispatch}
                    onClose={() => setOpenModalRowId(undefined)}
                />
            )}

            {(existingComparison || selectedComparisonColumn || addingComparisonColumn) && (
                <MetadataEditorComparisonModal
                    existingComparison={existingComparison}
                    selectedComparisonColumn={selectedComparisonColumn}
                    columns={columns}
                    getSampleValue={getSampleValue}
                    dispatch={dispatch}
                    onClose={() => {
                        setExistingComparison(undefined);
                        setSelectedComparisonColumn(undefined);
                        setAddingComparisonColumn(false);
                    }}
                />
            )}
        </>
    );
};
